import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { siteConfig } from '../data/config';
import SEO, { pageSEO } from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronLeft, AlertTriangle } from 'lucide-react';

const DisclaimerPage = () => {
  const { language, getRoute } = useLanguage();

  const content = {
    fr: {
      title: 'Avertissement',
      lastUpdated: 'Dernière mise à jour: Janvier 2026',
      sections: [
        {
          title: 'Exactitude des informations',
          content: `Les informations présentées sur ce site web sont fournies à titre informatif uniquement. Bien que nous nous efforcions de maintenir les informations à jour et exactes, ${siteConfig.businessName} ne fait aucune déclaration ou garantie quant à l'exactitude, l'exhaustivité ou l'actualité des informations.`
        },
        {
          title: 'Tarifs',
          content: `Les tarifs affichés sont des estimations basées sur les tarifs réglementés par la Commission des transports du Québec. Le tarif réel peut varier en fonction des conditions de circulation, de l'itinéraire emprunté et d'autres facteurs. Seul le taximètre fait foi pour le calcul du tarif final.`
        },
        {
          title: 'Disponibilité du service',
          content: `La disponibilité de nos services peut varier selon la demande, les conditions météorologiques et d'autres facteurs. Nous ne garantissons pas la disponibilité d'un véhicule à tout moment.`
        },
        {
          title: 'Liens externes',
          content: `Ce site peut contenir des liens vers des sites web tiers. Nous n'avons aucun contrôle sur le contenu de ces sites et déclinons toute responsabilité quant à leur contenu.`
        },
        {
          title: 'Limitation de responsabilité',
          content: `Dans les limites permises par la loi, ${siteConfig.businessName} décline toute responsabilité pour tout dommage direct, indirect, accessoire ou consécutif résultant de l'utilisation de ce site web ou de nos services.`
        },
      ],
    },
    en: {
      title: 'Disclaimer',
      lastUpdated: 'Last updated: January 2026',
      sections: [
        {
          title: 'Accuracy of Information',
          content: `The information presented on this website is provided for informational purposes only. While we strive to keep information current and accurate, ${siteConfig.businessName} makes no representations or warranties regarding the accuracy, completeness, or timeliness of the information.`
        },
        {
          title: 'Fares',
          content: `Displayed fares are estimates based on rates regulated by the Commission des transports du Québec. Actual fare may vary depending on traffic conditions, route taken, and other factors. Only the taximeter determines the final fare.`
        },
        {
          title: 'Service Availability',
          content: `Availability of our services may vary based on demand, weather conditions, and other factors. We do not guarantee vehicle availability at all times.`
        },
        {
          title: 'External Links',
          content: `This site may contain links to third-party websites. We have no control over the content of these sites and disclaim any responsibility for their content.`
        },
        {
          title: 'Limitation of Liability',
          content: `To the extent permitted by law, ${siteConfig.businessName} disclaims all liability for any direct, indirect, incidental, or consequential damages arising from the use of this website or our services.`
        },
      ],
    },
  };

  const c = content[language];

  return (
    <div className="min-h-screen pt-20 pb-20" data-testid="disclaimer-page">
      <SEO {...pageSEO.disclaimer} />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <Button asChild variant="ghost" className="mb-8">
          <Link to={getRoute('/')}>
            <ChevronLeft className="w-4 h-4 mr-2" />
            {language === 'fr' ? 'Retour' : 'Back'}
          </Link>
        </Button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-600 flex items-center justify-center">
            <AlertTriangle className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl text-foreground">{c.title}</h1>
            <p className="text-muted-foreground text-sm">{c.lastUpdated}</p>
          </div>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-8">
            <div className="space-y-8">
              {c.sections.map((section, index) => (
                <div key={index}>
                  <h2 className="font-heading font-bold text-xl text-foreground mb-3">
                    {section.title}
                  </h2>
                  <p className="text-muted-foreground">
                    {section.content}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DisclaimerPage;
